import { Button, Dialog, Input, sonner } from '@sr/ui-library';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  ChangePasswordAmplifyError,
  useChangePasswordAmplify,
} from '../../api/permissions/userApi';
import { allRulesPass } from '../../utils/auth/passwordPolicy';
import { InputWrapper } from '../InputWrapper';
import { PasswordRuleChecklist } from '../auth/cognito/password/PasswordRuleChecklist';

const { toast } = sonner;

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormErrors {
  confirmPassword?: string;
  newPassword?: string;
  oldPassword?: string;
}

/**
 * In-app password rotation for Cognito users (APP-1896). Cognito error
 * codes from `useChangePasswordAmplify` are mapped to inline field errors.
 */
export const ChangePasswordModal = ({
  isOpen,
  onClose,
}: ChangePasswordModalProps) => {
  const changePassword = useChangePasswordAmplify();
  const isSubmitting = changePassword.isPending;
  // Synchronous guard: `isPending` lags one render behind `mutateAsync`,
  // so back-to-back Enter-key submits could both pass without this ref.
  const isSubmittingRef = useRef(false);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  const resetMutation = changePassword.reset;

  useEffect(() => {
    if (isOpen) return;
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    isSubmittingRef.current = false;
    resetMutation();
  }, [isOpen, resetMutation]);

  const allRulesMet = allRulesPass(newPassword);

  const validate = (): FormErrors => {
    const next: FormErrors = {};

    if (!oldPassword) {
      next.oldPassword = 'Current password is required.';
    }

    if (!newPassword) {
      next.newPassword = 'New password is required.';
    } else if (!allRulesMet) {
      next.newPassword = 'New password does not meet all requirements.';
    } else if (newPassword === oldPassword) {
      next.newPassword =
        'New password must be different from your current password.';
    }

    if (!confirmPassword) {
      next.confirmPassword = 'Please confirm your new password.';
    } else if (confirmPassword !== newPassword) {
      next.confirmPassword = 'Passwords do not match.';
    }

    return next;
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    const localErrors = validate();

    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);

      return;
    }

    isSubmittingRef.current = true;
    setErrors({});

    try {
      await changePassword.mutateAsync({ newPassword, oldPassword });
      toast.success('Your password has been changed.');
      onClose();
    } catch (err) {
      const { code, message } = err as ChangePasswordAmplifyError;

      if (code === 'NotAuthorizedException') {
        setErrors({ oldPassword: message });
      } else if (
        code === 'InvalidPasswordException' ||
        code === 'InvalidParameterException'
      ) {
        setErrors({ newPassword: message });
      } else {
        // Not tied to a single field — surface as toast.
        toast.error(message);
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (isSubmitting) return;
    if (!open) onClose();
  };

  return (
    <Dialog
      heading="Change password"
      actionButtonProps={{
        children: 'Change password',
        disabled: isSubmitting,
        loading: isSubmitting,
        onClick: handleSubmit,
      }}
      cancelButtonProps={{
        children: 'Cancel',
        disabled: isSubmitting,
        onClick: onClose,
        variant: 'outline',
      }}
      className="max-w-md"
      description="Enter your current password and choose a new one."
      open={isOpen}
      showHeading
      onOpenChange={handleOpenChange}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <InputWrapper
          label="Current password"
          errorMessage={errors.oldPassword}
          isRequired
        >
          <Input
            id="change-password-current"
            autoComplete="current-password"
            maxLength={256}
            type="password"
            value={oldPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setOldPassword(e.target.value)
            }
          />
        </InputWrapper>

        <InputWrapper
          label="New password"
          errorMessage={errors.newPassword}
          isRequired
        >
          <Input
            id="change-password-new"
            autoComplete="new-password"
            maxLength={256}
            type="password"
            value={newPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setNewPassword(e.target.value)
            }
          />
        </InputWrapper>

        <PasswordRuleChecklist password={newPassword} />

        <InputWrapper
          label="Confirm new password"
          errorMessage={errors.confirmPassword}
          isRequired
        >
          <Input
            id="change-password-confirm"
            autoComplete="new-password"
            maxLength={256}
            type="password"
            value={confirmPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setConfirmPassword(e.target.value)
            }
          />
        </InputWrapper>

        {/* Hidden submit so Enter key submits the form */}
        <Button aria-hidden className="hidden" type="submit">
          Submit
        </Button>
      </form>
    </Dialog>
  );
};
